import React from "react";
import { ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { RouteComponentPropsHistory } from "../../types";

import { apiGet } from "../../Api";
import { formatDateTimeLocalNumeric } from "../../Utilities";
import withTitle from "../withTitle";

import HasRight from "../HasRight";
import Indicator from "../Indicator";
import ViewToolbar from "../ViewToolbar";

import "../../blocks.css";
import "../../icomoon.css";
import "../../indicator.css";
import "../../list.css";
import "../../spinner.css";
import "../../table.css";
import "../../user.css";
import "../viewer.css";
import DelayInput from "../DelayInput";

interface IUserAccountsViewState {
	isLoading: boolean;
	error?: string;
	users: any[];
	filteredUsers: any[];
	searchText: string;
	searchStatus: string;
}

class UserAccountsView extends React.Component<
	RouteComponentPropsHistory<any, any>,
	IUserAccountsViewState
> {
	constructor(props: Readonly<RouteComponentPropsHistory<any, any>>) {
		super(props);

		this.state = {
			isLoading: false,
			users: [],

			filteredUsers: [],
			searchText:
				(props.location.state && props.location.state.searchText) || "",
			searchStatus:
				(props.location.state && props.location.state.searchStatus) || "1",
		};

		this.changeSearchChange = this.changeSearchChange.bind(this);
	}

	public async componentDidMount() {
		try {
			this.setState({
				error: undefined,
				isLoading: true,
			});
			const users = await apiGet<any[]>("users?limit=1000");

			this.setState((s) => ({
				filteredUsers: this.filterUserList(
					users,
					s.searchText.toLowerCase(),
					s.searchStatus
				),
				isLoading: false,
				users,
			}));
		} catch (e) {
			this.setState({
				error: e.message,
				isLoading: false,
			});
		}
	}

	public render() {
		return (
			<div className="viewer-root">
				<ViewToolbar>
					<HasRight right="user:create-bare">
						<Link
							to="/users/add"
							className="icon-plus"
						/>
					</HasRight>
				</ViewToolbar>
				<div className="filter-control-box">
					<DelayInput
						type="search"
						className="fill-space"
						timeout={300}
						placeholder="Search..."
						name="searchText"
						value={this.state.searchText}
						onChangeDelayed={this.changeSearchChange}
					/>
					<select
						name="searchStatus"
						value={this.state.searchStatus}
						onChange={this.changeSearchChange}>
						<option value="">All Status</option>
						<option value="1">Only Enabled</option>
						<option value="0">Only Disabled</option>
					</select>
				</div>

				<table className="table-vertical">
					<thead>
						<tr>
							<th>Name</th>
							<th>Roles</th>
							<th>Last Active</th>
							<th className="col-smallest">App Version</th>
							<th className="col-smallest">Status</th>
							<th className="col-smallest" />
						</tr>
					</thead>
					<thead className="table-vertical-head-small">
						<tr>
							<th>Users</th>
						</tr>
					</thead>
					<tbody>
						{this.state.isLoading ? (
							<tr>
								<td colSpan={10}>
									<div className="spinner-center lds-dual-ring" />
								</td>
							</tr>
						) : (
							this.state.filteredUsers.map((a) => (
								<tr key={a.id}>
									<td>
										<div className="user-info-block">
											<div className="user-avatar">
												{this.userNameAvatar(a)}
											</div>
											<div>
												<div>{a.displayName}</div>
												<div className="small-text">{a.username}</div>
											</div>
										</div>
									</td>
									<td className="wrap">
										{a.roles.map((r: any) => (
											<Indicator
												color="lightblue"
												key={r.id}
												text={r.name}
											/>
										))}
									</td>
									<td
										className="tbl-vertical-subtext inline wrap"
										data-caption="Last active: ">
										{formatDateTimeLocalNumeric(a.lastActive) || "-"}
									</td>
									<td
										className="tbl-vertical-subtext inline col-center"
										data-caption="App version: ">
										{a.lastVersion || "-"}
									</td>
									<td className="tbl-vertical-subtext">
										<Indicator
											color={a.status === 0 ? "red" : "green"}
											icon={a.status === 0 ? "times" : "check"}
											text={this.statusToText(a.status)}
										/>
									</td>
									<td>
										<HasRight right="user:write">
											<Link
												to={`/users/${a.id}`}
												className="button small text-with-icon">
												<span>Details</span>
												<i className="icon-chevron-right" />
											</Link>
										</HasRight>
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>
		);
	}

	private statusToText(status: number) {
		switch (status) {
			case 0:
				return "Disabled";
			case 1:
				return "Enabled";
			case 2:
				return "Suspended";
			default:
				return (status || "Unknown").toString();
		}
	}

	private userNameAvatar(user: any) {
		const parts = (user.displayName || user.username).split(" ");
		let text = parts[0][0].toUpperCase();
		if (parts.length >= 2) {
			text += parts[1][0].toUpperCase();
		}

		return text;
	}

	private changeSearchChange(
		e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>
	) {
		const searchValue = e.currentTarget.value;
		const searchName = e.currentTarget.name;

		this.setState(
			{
				[searchName]: searchValue,
			} as any,
			() => {
				this.saveSearchState();

				this.setState({
					filteredUsers: this.filterUserList(
						this.state.users,
						this.state.searchText.toLowerCase(),
						this.state.searchStatus
					),
				});
			}
		);
	}

	private filterUserList(
		list: any[],
		searchValue: string,
		searchStatus: string
	) {
		if (!searchValue && !searchStatus) {
			return list;
		}

		return list.filter(
			(u) =>
				(u.username.includes(searchValue) ||
					u.displayName.toLowerCase().includes(searchValue) ||
					!!(u.roles as any[]).find((r) =>
						r.name.toLowerCase().includes(searchValue)
					)) &&
				(!searchStatus || u.status === +searchStatus)
		);
	}

	private saveSearchState() {
		const location = this.props.location;
		location.state = {
			searchText: this.state.searchText,
			searchStatus: this.state.searchStatus,
		};

		this.props.history.replace(location);
	}
}

export default withTitle("User Management", UserAccountsView);
